/**
 * Centralized Scoring & Award Calculator for APCS
 * 
 * Single source of truth for:
 * - Effective jury score extraction (adminAdjustedScore fallback)
 * - Raw average calculation
 * - Video duration auto-penalty calculation (with sub-second grace period)
 * - Manual penalty calculation
 * - Total penalty calculation
 * - Penalized final average calculation
 * - Award tier determination
 * - Master registrant score & award calculation
 */

import { findVideoPenaltyRule } from './videoPenaltyRules.js';

const formatDuration = (durationSeconds) => {
    const totalMilliseconds = Math.round(Number(durationSeconds) * 1000);
    const minutes = Math.floor(totalMilliseconds / 60000);
    const remainingMilliseconds = totalMilliseconds % 60000;
    const seconds = Math.floor(remainingMilliseconds / 1000);
    const milliseconds = remainingMilliseconds % 1000;
    const baseDuration = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    return milliseconds > 0
        ? `${baseDuration}.${String(milliseconds).padStart(3, '0')}`
        : baseDuration;
};

/**
 * Extract the effective numeric score from a score document or value.
 * Uses adminAdjustedScore if available and valid, otherwise falls back to score.
 * 
 * @param {Object|number} scoreItem - Score document or numeric score
 * @returns {number|null} Valid numeric score or null
 */
export const getEffectiveScore = (scoreItem) => {
    if (scoreItem === null || scoreItem === undefined) return null;
    if (typeof scoreItem === 'number') {
        return isNaN(scoreItem) ? null : scoreItem;
    }
    if (typeof scoreItem === 'object') {
        if (scoreItem.adminAdjustedScore !== undefined && scoreItem.adminAdjustedScore !== null) {
            const adjusted = Number(scoreItem.adminAdjustedScore);
            return isNaN(adjusted) ? null : adjusted;
        }
        if (scoreItem.score !== undefined && scoreItem.score !== null) {
            const original = Number(scoreItem.score);
            return isNaN(original) ? null : original;
        }
    }
    const num = Number(scoreItem);
    return isNaN(num) ? null : num;
};

/**
 * Calculate the raw unpenalized average score of valid jury evaluations.
 * Rounded to 2 decimal places.
 * 
 * @param {Array} scores - Array of score documents or numbers
 * @returns {number|null} Raw average score (0-100) or null if no valid scores
 */
export const calculateRawAverage = (scores) => {
    if (!Array.isArray(scores) || scores.length === 0) return null;

    const validScores = scores
        .map(getEffectiveScore)
        .filter((s) => typeof s === 'number' && !isNaN(s));

    if (validScores.length === 0) return null;

    const sum = validScores.reduce((acc, score) => acc + score, 0);
    return Math.round((sum / validScores.length) * 100) / 100;
};

/**
 * Calculate the automatic video duration penalty using the registrant's exact
 * registration identity and the event-specific video penalty configuration.
 * Includes sub-second grace period: videos up to threshold + fractional second (e.g. <= 600.999s)
 * are NOT penalized. Only penalize if integer seconds exceed threshold (i.e. >= 601s for 10m).
 * 
 * @param {Object} registrant - Registrant document data
 * @param {Object|null} videoPenaltyConfig - Event-specific penalty configuration
 * @returns {Object} Video penalty result and rule-matching status
 */
export const calculateVideoPenalty = (registrant, videoPenaltyConfig) => {
    if (!videoPenaltyConfig || !Array.isArray(videoPenaltyConfig.rules)) {
        return {
            autoPenaltyScore: 0,
            autoPenaltyReason: '',
            videoPenaltyRuleStatus: 'missing-config',
            videoPenaltyMaximumMinutes: null,
            matchedVideoPenaltyRule: null,
        };
    }

    const matchedRule = findVideoPenaltyRule(registrant, videoPenaltyConfig);
    if (!matchedRule) {
        return {
            autoPenaltyScore: 0,
            autoPenaltyReason: '',
            videoPenaltyRuleStatus: 'missing-rule',
            videoPenaltyMaximumMinutes: null,
            matchedVideoPenaltyRule: null,
        };
    }

    const thresholdMins = Number(matchedRule.maximumMinutes);
    const penaltyVal = Math.abs(Number(videoPenaltyConfig.penaltyScore || 0));
    const thresholdSec = thresholdMins * 60;
    const videoDurationSec = Number(registrant?.videoDuration);

    if (Number.isFinite(videoDurationSec) && Math.floor(videoDurationSec) > thresholdSec) {
        return {
            autoPenaltyScore: penaltyVal,
            autoPenaltyReason: `Video duration (${formatDuration(videoDurationSec)}) exceeded ${formatDuration(thresholdSec)} limit`,
            videoPenaltyRuleStatus: 'matched',
            videoPenaltyMaximumMinutes: thresholdMins,
            matchedVideoPenaltyRule: matchedRule,
        };
    }

    return {
        autoPenaltyScore: 0,
        autoPenaltyReason: '',
        videoPenaltyRuleStatus: 'matched',
        videoPenaltyMaximumMinutes: thresholdMins,
        matchedVideoPenaltyRule: matchedRule,
    };
};

/**
 * Calculate manual penalty applied directly by admin to the registrant.
 * 
 * @param {Object} registrant - Registrant document data
 * @returns {Object} { manualPenaltyScore: number, manualPenaltyReason: string }
 */
export const calculateManualPenalty = (registrant) => {
    const manualPenaltyScore = Math.abs(Number(registrant?.penaltyScore || 0));
    const manualPenaltyReason = registrant?.penaltyReason || '';

    return {
        manualPenaltyScore,
        manualPenaltyReason
    };
};

/**
 * Calculate total penalty combining auto video penalty and manual penalty.
 * 
 * @param {Object} registrant - Registrant document data
 * @param {Object|null} videoPenaltyConfig - Event-specific penalty configuration
 * @returns {Object}
 */
export const calculateTotalPenalty = (registrant, videoPenaltyConfig) => {
    const videoPenalty = calculateVideoPenalty(registrant, videoPenaltyConfig);
    const { autoPenaltyScore, autoPenaltyReason } = videoPenalty;
    const { manualPenaltyScore, manualPenaltyReason } = calculateManualPenalty(registrant);

    const totalPenaltyScore = manualPenaltyScore + autoPenaltyScore;

    return {
        autoPenaltyScore,
        autoPenaltyReason,
        manualPenaltyScore,
        manualPenaltyReason,
        totalPenaltyScore,
        videoPenaltyRuleStatus: videoPenalty.videoPenaltyRuleStatus,
        videoPenaltyMaximumMinutes: videoPenalty.videoPenaltyMaximumMinutes,
        matchedVideoPenaltyRule: videoPenalty.matchedVideoPenaltyRule,
    };
};

/**
 * Standardized award tier calculation based on final average score.
 * 
 * Tiers:
 * - 99 to 100: Sapphire
 * - 93 to <99: Diamond
 * - 87 to <93: Gold
 * - 80 to <87: Silver
 * - 0 to <80: Fail
 * - Otherwise: N/A
 * 
 * @param {number|string} score - Final penalized score
 * @returns {string} Award name
 */
export const calculateAward = (score) => {
    if (score === null || score === undefined || score === '' || score === '-') return "N/A";
    const num = Number(score);
    if (isNaN(num)) return "N/A";

    if (num >= 99 && num <= 100) return "Sapphire";
    if (num >= 93 && num < 99) return "Diamond";
    if (num >= 87 && num < 93) return "Gold";
    if (num >= 80 && num < 87) return "Silver";
    if (num >= 0 && num < 80) return "Fail";
    return "N/A";
};

/**
 * Master calculation function for a registrant's scores, penalties, and award.
 * Used identically by ScoringRecap, RegistrantDashboard, and Sync Awards.
 * 
 * @param {Array} scores - Array of score documents or numbers
 * @param {Object} registrant - Registrant document data
 * @param {Object|null} videoPenaltyConfig - Event-specific penalty configuration
 * @returns {Object} Complete calculation result
 */
export const calculateRegistrantScoreAndAward = (scores, registrant, videoPenaltyConfig) => {
    const rawAverage = calculateRawAverage(scores);
    const penalties = calculateTotalPenalty(registrant, videoPenaltyConfig);

    let finalAverageScore = null;
    let finalAward = 'N/A';

    if (rawAverage !== null) {
        if (penalties.totalPenaltyScore > 0) {
            finalAverageScore = Math.round((rawAverage - penalties.totalPenaltyScore) * 100) / 100;
        } else {
            finalAverageScore = rawAverage;
        }
        finalAward = calculateAward(finalAverageScore);
    }

    return {
        rawAverage,
        finalAverageScore,
        manualPenaltyScore: penalties.manualPenaltyScore,
        manualPenaltyReason: penalties.manualPenaltyReason,
        autoPenaltyScore: penalties.autoPenaltyScore,
        autoPenaltyReason: penalties.autoPenaltyReason,
        totalPenaltyScore: penalties.totalPenaltyScore,
        videoPenaltyRuleStatus: penalties.videoPenaltyRuleStatus,
        videoPenaltyMaximumMinutes: penalties.videoPenaltyMaximumMinutes,
        matchedVideoPenaltyRule: penalties.matchedVideoPenaltyRule,
        videoPenaltyConfigRevision: Number(videoPenaltyConfig?.revision) || null,
        finalAward,
        hasScores: rawAverage !== null,
        scoresCount: Array.isArray(scores) ? scores.length : 0
    };
};

/**
 * Determine whether a persisted registrant result differs from the current
 * centralized calculation or was produced by an older rule revision.
 */
export const isAwardSyncNeeded = (registrant, scoring) => {
    if (!scoring?.hasScores) return false;

    const persistedAverage = Number(registrant?.averageScore);
    const calculatedAverage = Number(scoring.finalAverageScore);
    const averageMatches = Number.isFinite(persistedAverage)
        && Number.isFinite(calculatedAverage)
        && Math.abs(persistedAverage - calculatedAverage) < 0.001;
    const awardMatches = registrant?.finalAward === scoring.finalAward;
    const revisionMatches = Number(registrant?.videoPenaltyConfigRevision)
        === Number(scoring.videoPenaltyConfigRevision);

    return !averageMatches || !awardMatches || !revisionMatches;
};
